/**
 * What this player has bought — or has been comped.
 *
 * THIS IS A SEAM, NOT A BILLING SYSTEM. Premium decks are sold as one-time
 * purchases, and the decision of web-only (Stripe) versus native-wrapped
 * (Apple/Google IAP, most likely unified behind RevenueCat) has not been made —
 * see PRODUCTION_LAUNCH_PLAN.md §B1/B2, where it is called out as a fork that
 * changes the processor, the cut and the store-review surface. Building either
 * one before that is decided is work thrown away.
 *
 * So the shape is here and the paid source is not. When billing lands, the real
 * entitlement is written onto the player document by a webhook and read from
 * there; `ownsSku()` becomes a read of that, and nothing above it changes.
 *
 * WHY THE PAID STORE MUST NOT BE localStorage IN THE END. The unlock economy
 * already evaluates client-side against a local profile, which is fine for
 * something a player earned: forging it only cheats themselves. A purchase is
 * different in two ways that both matter more than piracy — it has to SURVIVE a
 * reinstall and FOLLOW the account to a new device, and neither is possible from
 * local state. Cosmetics are not worth server-side enforcement of rendering, but
 * they are worth server-side ownership.
 *
 * SO IN PRODUCTION THERE IS EXACTLY ONE WAY TO HOLD A PREMIUM DECK: being on the
 * server's `allowedHosts` list. There used to be a second — a `?entitle=<deckId>`
 * URL override kept in localStorage — and it was removed rather than left
 * inert. It was a client-side grant on a client-side check, which is fine for
 * something cosmetic and fine for a dev hatch, but it meant the honest answer to
 * "how does a player get this deck" was two answers, one of which was a URL. To
 * look at a freshly-built deck locally, sign in as a seeded host instead
 * (`scripts/seed-dev-hosts.mjs`); on a deployed build, be on the list.
 *
 * A dev or local build also has a simulated purchase (`billing/release.ts`): the
 * server records what was bought, and `GET /host/status` reports it here.
 */
import { billingVisible } from "../billing/release";

/** Stock-keeping unit for one purchasable item. Decks are `deck.<themeId>`. */
export type Sku = string;

export const deckSku = (themeId: string): Sku => `deck.${themeId}`;

// ---------------------------------------------------------------------------
// Complimentary access for approved hosts
// ---------------------------------------------------------------------------

/**
 * Approved hosts get every premium deck, free.
 *
 * `allowedHosts` is the server's list of accounts cleared to host online games.
 * It is a small, hand-curated set — the people running real tables — so comping
 * them the cosmetics costs nothing and puts the decks in front of exactly the
 * players whose screens other people are looking at.
 *
 * IT IS A CAPABILITY, NOT A PURCHASE, and the two are kept apart deliberately.
 * A comp is granted by an allow-list the player does not control and can be
 * withdrawn by an admin removing them from it; a purchase is owned. Modelling
 * this as "silently write the SKUs into their owned set" would make the two
 * indistinguishable, and then revoking hosting would look like confiscating
 * something they had bought. `evaluateUnlock` asks both questions separately.
 *
 * There is deliberately no enumeration of which SKUs are comped: it is every
 * premium deck, including ones added later. Listing them here would also make
 * this module import the deck catalog, which imports unlocks, which imports
 * this — a cycle for no gain.
 */
export const HOST_COMP_KEY = "fiveout:hostComp";

/**
 * Cached in localStorage so it can be read SYNCHRONOUSLY at boot.
 *
 * `evaluateUnlock` is called during render, from `themesWithUnlocks()` and from
 * `getSelectedThemeId()`, and cannot await an HTTP round trip. Without a cache,
 * every launch would start with the comp unknown, `getSelectedThemeId()` would
 * find the stored premium deck "not unlocked" and fall back to a starter, and a
 * host who dealt quickly would get the wrong cards — then watch the table
 * repaint when the answer arrived.
 *
 * The server is still the authority; this is just what we believed last time.
 */
let hostComp = readHostComp();

function readHostComp(): boolean {
  try {
    return localStorage.getItem(HOST_COMP_KEY) === "true";
  } catch {
    return false;
  }
}

function writeHostComp(value: boolean): void {
  if (value === hostComp) return;
  hostComp = value;
  try {
    if (value) localStorage.setItem(HOST_COMP_KEY, "true");
    else localStorage.removeItem(HOST_COMP_KEY);
  } catch {
    /* storage unavailable — the flag simply doesn't survive the session */
  }
  for (const listener of listeners) listener();
}

/** True when this account is an approved host, and so gets the premium decks. */
export function hasHostComplimentaryAccess(): boolean {
  return hostComp;
}

const listeners = new Set<() => void>();

/** Subscribes to entitlement changes. Returns the unsubscribe. */
export function subscribeEntitlements(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Asks the server whether this account is an approved host, and caches it.
 *
 * Called on every auth change (see `initAccountStore`). Three outcomes, and the
 * middle one is the one that matters:
 *
 *   allowedHost true   -> comp granted
 *   allowedHost false  -> comp revoked. An authoritative no is the only thing
 *                         that takes it away.
 *   null (any failure) -> LEAVE THE CACHE ALONE. Offline, server restarting, a
 *                         transient 500: none of those mean the account lost
 *                         anything, and clearing on them would make a player's
 *                         deck flicker away every time their train went into a
 *                         tunnel. Local play works with no network at all
 *                         (that is why the card font is self-hosted), so this
 *                         path is ordinary, not exceptional.
 */
export async function refreshHostEntitlement(): Promise<void> {
  /*
   * Imported here rather than at the top of the file, and it matters more than
   * a lazy import usually does: `net/onlineClient` reaches Firebase, which
   * builds an auth client the moment its module is evaluated. A static import
   * would put that at the root of everything that asks what is unlocked —
   * `unlocks.ts`, `themes.ts`, and now the round recorder — so a question about
   * a player's own catalog would drag the network stack in behind it.
   *
   * The only thing in this file that needs the server is this one call, and it
   * is already async.
   */
  const { fetchHostStatus } = await import("../net/onlineClient");
  const status = await fetchHostStatus();
  if (status === null) return;
  writeHostComp(status.allowedHost);
  applyBillingStatus(status.billing ?? null);
}

/** Signed out: no account, so no comp and nothing owned. Called from the auth listener. */
export function clearHostEntitlement(): void {
  writeHostComp(false);
  applyBillingStatus(null);
}

// ---------------------------------------------------------------------------
// Purchases
// ---------------------------------------------------------------------------

/** What `GET /host/status` says this account has bought and may host. Dev and local only. */
export interface BillingStatus {
  plan: string;
  hostingEndMs: number;
  bundleOwned: boolean;
  bundleHostingEndMs: number;
  ownedSkus: Sku[];
  paidHosting: boolean;
  comp: boolean;
  /** 0 means no limit beyond the party's. */
  joinerLimit: number;
  /** 0 means no limit. */
  tableLimit: number;
  hostedTables: { gameId: string; code: string }[];
}

/** The owned SKUs, cached for the same reason as the comp: `evaluateUnlock` runs during render. */
export const OWNED_SKUS_KEY = "fiveout:ownedSkus";

let ownedSkus = readOwnedSkus();
let billingStatus: BillingStatus | null = null;

function readOwnedSkus(): ReadonlySet<Sku> {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(OWNED_SKUS_KEY) ?? "[]");
    return new Set(Array.isArray(raw) ? raw.filter((s): s is Sku => typeof s === "string") : []);
  } catch {
    return new Set();
  }
}

/** The account's last known billing state. Null until the server answers, and for a signed-out player. */
export function getBillingStatus(): BillingStatus | null {
  return billingStatus;
}

/** The server's answer is the whole truth: what it no longer lists is no longer owned. */
export function applyBillingStatus(status: BillingStatus | null): void {
  // An unchanged answer tells nobody: every listener rebuilds a deck catalog.
  if (JSON.stringify(status) === JSON.stringify(billingStatus)) return;
  billingStatus = status;
  ownedSkus = new Set(status?.ownedSkus ?? []);
  try {
    if (ownedSkus.size > 0) localStorage.setItem(OWNED_SKUS_KEY, JSON.stringify([...ownedSkus]));
    else localStorage.removeItem(OWNED_SKUS_KEY);
  } catch {
    /* storage unavailable — ownership simply doesn't survive the session */
  }
  for (const listener of listeners) listener();
}

/**
 * Whether this player OWNS a SKU outright, as opposed to being comped it.
 *
 * `evaluateUnlock` asks this separately from the host comp, so nothing above it
 * has to learn the difference between owning a deck and being lent one. Always
 * false in production, whatever a device has cached.
 */
export function ownsSku(sku: Sku): boolean {
  return billingVisible() && ownedSkus.has(sku);
}

/**
 * Clears the `?entitle=` override's storage key. Call once, at boot.
 *
 * The override is gone (see the header), but a device that used it still has the
 * key sitting in localStorage. Nothing reads it any more, so this is tidying
 * rather than enforcement — but a stale key that once granted a deck is exactly
 * the thing somebody finds in devtools later and spends an afternoon on.
 */
export function clearLegacyEntitlements(): void {
  try {
    localStorage.removeItem(LEGACY_DEV_ENTITLEMENTS_KEY);
  } catch {
    /* storage unavailable — nothing to clean up */
  }
}

const LEGACY_DEV_ENTITLEMENTS_KEY = "fiveout:devEntitlements";
